/**
 * Regole di instradamento e lettura degli errori delle chiamate al core (16 sez. 3.1, 12 R-09). Modulo puro,
 * usato da coreCall in src/lib/services/gatewayService.ts.
 * - Le rotte del motore e-commerce vanno SEMPRE sul core (non esiste una edge equivalente): regola a
 *   prefisso, perche' le rotte con id (commerce/stores/<id>/pause) non possono stare in un Set esatto.
 * - Gli errori del motore hanno la forma { error: { type, code, message } } con message in italiano:
 *   si restituisce anche il codice, cosi' l'interfaccia traduce per codice (src/lib/commerce/errorText.ts).
 */

const CORE_PREFIXES = ['commerce/', 'admin/commerce/'] as const;

export function isCoreOnlyRoute(name: string, exact: ReadonlySet<string>): boolean {
  if (exact.has(name)) return true;
  return CORE_PREFIXES.some((p) => name.startsWith(p) && name.length > p.length);
}

export interface CoreErrorInfo {
  message: string;
  code: string | null;
  status: number;
}

const str = (v: unknown): string | null => (typeof v === 'string' && v.length > 0 ? v : null);

export function parseCoreError(body: string, status: number): CoreErrorInfo {
  const fallback = `HTTP ${status}`;
  let parsed: unknown;
  try {
    parsed = JSON.parse(body);
  } catch {
    return { message: body || fallback, code: null, status };
  }
  if (typeof parsed !== 'object' || parsed === null) return { message: body || fallback, code: null, status };
  const p = parsed as Record<string, unknown>;
  const e = p.error;
  if (typeof e === 'object' && e !== null) {
    const eo = e as Record<string, unknown>;
    return { message: str(eo.message) ?? str(p.message) ?? fallback, code: str(eo.code), status };
  }
  return { message: str(e) ?? str(p.message) ?? fallback, code: str(p.code), status };
}

/**
 * Errore di una colonna che non esiste ancora (migration non applicata): 42703 di Postgres o
 * PGRST204 di PostgREST, con il nome della colonna nel testo. Serve ai filtri del vecchio sistema
 * WooCommerce (commerce_store_id IS NULL), che non devono rompere le schermate prima della migration.
 */
export function isMissingColumnError(err: { code?: string | null; message?: string | null } | null | undefined, column: string): boolean {
  if (!err) return false;
  const code = err.code ?? '';
  if (code !== '42703' && code !== 'PGRST204') return false;
  return (err.message ?? '').includes(column);
}

/**
 * Codice da usare nell'interfaccia per un errore di coreCall: il codice del core se c'e'; un 404 senza
 * codice e' il fallback del core (rotta non ancora montata); senza stato e' un problema di rete.
 */
export function failureCode(code: string | null | undefined, status: number | null | undefined): string {
  if (code) return code;
  if (status === 404) return 'route_not_found';
  if (status === 401) return 'not_authenticated';
  if (status === 403) return 'not_allowed';
  if (status === 429) return 'rate_limited';
  if (status === undefined || status === null) return 'network_error';
  return 'internal_error';
}
